<template>
    <div class="card mt-4">
        <div class="card-body">
            <h3 class="mb-1">Fiscal Year & Donor Settings</h3>
            <p class="mb-3">Need more information about setting your Fiscal Year and Reporting Period?
                <a href="https://help.avidai.com/hc/en-us/articles/42928674848403-Reporting-Period-Classifications" target="_blank" rel="noopener"> Check Avid's Knowledge Base.</a>
            </p>
            <div class="row">   
                <div class="col-md-4 col-12 mb-4">
                    <div class="form-group">
                        <label class="form-label">Fiscal Year Start Month</label>
                        <select 
                            class="form-select" 
                            name="local_client.fiscal_year_offset" 
                            v-model="local_client.fiscal_year_offset"
                            :disabled="!canEdit('settings')"
                         aria-label="Fiscal Year Start Month">
                            <option v-for="(month, index) in months" :key="index" :value="index">
                                {{ month }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-md-4 col-12 mb-3">
                    <div class="form-group">
                        <label class="form-label">Donor Level Criteria </label>
                        <select 
                            class="form-select" 
                            name="local_client.giving_levels.split" 
                            v-model="local_client.giving_levels.split"
                            :disabled="!canEdit('settings')"
                         aria-label="Donor Level Criteria">
                            <option value="HAC">Highest Annual Cumulative Giving</option>
                            <option value="HPC">Highest Previous Gift</option>
                        </select>
                    </div>
                </div>
                <div class="col-md-4 col-12 mb-3">
                    <div class="form-group">
                        <label class="form-label">Preferred Reporting Period</label>
                        <select 
                            class="form-select" 
                            name="local_client.default_reporting_period" 
                            v-model="local_client.default_reporting_period"
                            :disabled="!canEdit('settings')"
                         aria-label="Preferred Reporting Period">
                            <option v-for="period in reportingPeriods" :key="period" :value="period">
                                {{ period }}
                            </option>
                        </select>
                    </div>
                </div>                        
            </div>
            <div class="mb-3 table-card">
                <div class="row">
                    <div class="col-lg-6 col-12 mb-2">
                        <label class="form-label">Giving Levels</label>
                    </div>
                    <div class="col-lg-6 d-none d-lg-block text-end mb-2">
                        <button 
                            class="btn btn-primary btn-sm" 
                            @click="addLevel" 
                            v-if="edit_index === -1 && canEdit('settings')"
                        >
                            <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add"></img> Add Level
                        </button>
                    </div>
                </div>
                <div class="card-body p-0">
                    <div class="table-width-responsive">
                        <table class="table table-without-pagination mb-0">
                            <thead>
                                <tr class="table-header-row">
                                    <th class="d-table-cell">Name</th>
                                    <th class="d-none d-sm-table-cell">Minimum Threshold</th>
                                    <th 
                                        v-if="local_client.giving_levels.levels.length > 3"
                                        class="d-none d-md-table-cell"
                                    >
                                        Report on Upgrades?
                                        <img class="info-icon" src="/img/icons/avid-icons/info.svg" 
                                            data-bs-toggle="tooltip" :title="upgradeTooltip" alt=""
                                        ></img>
                                    </th>
                                    <th class="d-table-cell column-sort-disabled pe-3" width="1" v-if="canEdit('settings')">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(level, index) in local_client.giving_levels.levels" :key="index">
                                    <td class="d-table-cell">
                                        <template v-if="index !== edit_index">
                                            {{ level.name }}
                                        </template>
                                        <input 
                                            v-else
                                            type="text" 
                                            class="form-control form-control-sm" 
                                            v-model="local_level.name" 
                                            placeholder="" 
                                            @change="changeDonorLevelInputs"
                                         aria-label="Text input">
                                    </td>
                                    <td class="d-none d-sm-table-cell">
                                        <template v-if="index !== edit_index">
                                            <span v-if="index === local_client.giving_levels.levels.length-1">&mdash;</span>
                                            <span v-else>{{ currency(level.min) }}</span>
                                        </template>
                                        <div 
                                            v-else
                                            v-if="index !== local_client.giving_levels.levels.length-1"
                                        >
                                            <input 
                                                type="text" 
                                                class="form-control" 
                                                v-model="local_level.min"
                                                @keypress="isPositiveNumber" 
                                                @paste="cleanUpPaste" 
                                                @change="changeDonorLevelInputs"
                                             aria-label="Text input">
                                        </div>
                                    </td>
                                    <td 
                                        v-if="local_client.giving_levels.levels.length > 3"
                                        class="d-none d-md-table-cell"
                                    >
                                        <div 
                                            v-if="index !== local_client.giving_levels.levels.length-1"
                                            class="checkbox-animated mt-1 mb-0 ms-2"
                                        >
                                            <input 
                                                :disabled="!canEdit('settings')"
                                                :id="`checkbox_animated_${index}`" 
                                                type="checkbox" 
                                                v-model="level.report" 
                                                @change="limitChecks(index)"
                                             aria-label="Checkbox">
                                            <label :for="`checkbox_animated_${index}`">
                                                <span class="check"></span>
                                                <span class="box"></span>
                                            </label>
                                        </div>
                                    </td>
                                    <td class="d-table-cell" v-if="canEdit('settings')">
                                        <div class="dropdown d-flex justify-content-center" v-if="edit_index !== index">
                                            <button 
                                                class="btn btn-white btn-icon-only-sm" 
                                                data-bs-toggle="dropdown" 
                                                data-bs-auto-close="true" 
                                                aria-expanded="false"
                                            >
                                                <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" alt="More options" data-bs-toggle="tooltip" title="Actions">
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-end">
                                                <button 
                                                    class="dropdown-item d-none d-md-block" 
                                                    @click="editLevel(index)"
                                                >
                                                    <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt="Edit">
                                                    Edit
                                                </button>
                                                <button 
                                                    v-if="index !== local_client.giving_levels.levels.length-1"
                                                    class="dropdown-item text-danger" 
                                                    @click="deleteLevel(index)"
                                                >
                                                    <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt="Delete">
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                        <div v-else class="d-flex justify-content-center">
                                            <button class="btn btn-white btn-icon-only-sm" @click="cancelLevel(index)" data-bs-toggle="tooltip" title="Cancel" aria-label="Cancel">
                                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>                    
                    </div>
                </div>
            </div>

            <div v-if="errors.giving_levels" class="text-danger invalid-feedback">
                {{ errors.giving_levels }}
            </div>
            
            <!-- Save Button -->
            <div class="mt-4" v-if="saveEnabled">
                <button 
                    type="button" 
                    class="btn btn-primary px-4" 
                    @click.prevent="save()" 
                    :disabled="form.busy || !canEdit('settings')"
                >
                    <img v-if="!form.busy" class="icon me-1" src="/img/icons/avid-icons/save.svg" alt="">
                    <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                        <span class="visually-hidden">Saving...</span>
                    </div> Save
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import permissionsMixin from '../../../mixins/permissions';
import { useNotifications } from '../../../composables/useNotifications';

export default defineComponent({
    name: 'FiscalYearSettings',
    mixins: [permissionsMixin],
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        client: {
            type: Object,
            required: true
        },
        saveEnabled: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            local_client: {},
            edit_index: -1,
            form: {
                successful: false,
                busy: false,
                error: false
            },
            errors: {
                giving_levels: ''
            },
            if_can_save_new_level: true,
            months: [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ],
            reportingPeriods: [
                'Calendar Year to Date',
                'Fiscal Year to Date',
                'Trailing 12 Months'
            ],
            upgradeTooltip: 'AVID has special reporting set up to monitor donors who upgrade to a higher giving level. However, only two giving levels can be reported on at a time. Select those two levels below.'
        };
    },
    beforeMount() {
        this.local_client = this.client;

        if(this.local_client.giving_levels == null)
            this.local_client.giving_levels = this.getDefaultGivingLevels();
    },
    methods: {
        getDefaultGivingLevels() {
            return {
                split: 'HPC',
                levels: [
                    { name:"Major", min:10000, report: true },
                    { name:"Midlevel",min:1000, report: true },
                    { name:"Broadbase",min:0, report: false }
                ]
            };
        },
        isPositiveNumber(evt) {
            evt = (evt) ? evt : window.event;
            const charCode = (evt.which) ? evt.which : evt.keyCode;
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
                evt.preventDefault();
            } else {
                return true;
            }
        },
        limitChecks(index) {
            //If there are more than two levels with report as true...
            let count = 0;
            for(let i = 0; i < this.local_client.giving_levels.levels.length; i++)
                if(this.local_client.giving_levels.levels[i].report)
                    count++;

            //If there are too many checks
            if(count >= 3) {
                //Skip the first one and then set next one encountered that is true to false
                for(let i = 1; i < this.local_client.giving_levels.levels.length; i++)
                    if(i != index && this.local_client.giving_levels.levels[i].report) {
                        this.local_client.giving_levels.levels[i].report = false;
                        break;
                    }
            }
        },
        editLevel(index) {
            this.errors.giving_levels = '';
            this.local_level = {
                name: this.local_client.giving_levels.levels[index].name,
                min: this.local_client.giving_levels.levels[index].min,
                report: this.local_client.giving_levels.levels[index].report
            };
            this.edit_index = index;
        },
        deleteLevel(index) {
            this.local_client.giving_levels.levels.splice(index, 1);
            this.resetAndSortLevels();
        },
        saveLevel(index) {
            if (!this.checkIfCanSaveLevel(index)) {
                return;
            }
            this.local_client.giving_levels.levels[index].name = this.local_level.name.trim();
            this.local_client.giving_levels.levels[index].min = parseFloat(this.local_level.min);
            this.resetAndSortLevels();
            this.resetLocalLevel();
            this.edit_index = -1;
            this.form.busy = false;
            this.errors.giving_levels = '';
        },
        cancelLevel(index) {
            this.edit_index = -1;
            this.form.busy = false;

            //If it was a newly added level, remove it
            if(this.local_client.giving_levels.levels[index].name == "" && !this.checkIfCanSaveLevel(index))
                this.local_client.giving_levels.levels.splice(index, 1);
            this.errors.giving_levels = '';
            this.resetLocalLevel()
        },
        addLevel() {
            this.edit_index = this.local_client.giving_levels.levels.length-1;

            this.local_client.giving_levels.levels.splice(this.edit_index, 0, {
                name: '',
                min: null,
                report: this.local_client.giving_levels.levels.length < 3 ? true : false
            });

            this.resetLocalLevel()
        },
        checkIfCanSaveLevel(index) {
            const has_name = this.local_level.name != null && this.local_level.name.trim() !== '';

            let is_unique_name = true;
            for(let i = 0; i < this.local_client.giving_levels.levels.length; i++)
                if(i != index && this.local_client.giving_levels.levels[i].name == this.local_level.name) {
                    is_unique_name = false;
                    break;
                }

            const has_min = this.local_level.min != null && this.local_level.min !== '' && !isNaN(parseFloat(this.local_level.min));
            const isLastLevel = index === this.local_client.giving_levels.levels.length - 1;
            const minGreaterThanZero = isLastLevel || parseFloat(this.local_level.min) > 0;

            let is_unique_num = true;
            for(let i = 0; i < this.local_client.giving_levels.levels.length; i++)
                if(i != index && this.local_client.giving_levels.levels[i].min == this.local_level.min) {
                    is_unique_num = false;
                    break;
                }

            this.errors.giving_levels = '';
            if (!has_name) {
                this.errors.giving_levels = 'Donor Level Name cannot be empty';
            } else if (!is_unique_name) {
                this.errors.giving_levels = 'Donor Level Name cannot be duplicate';
            } else if (!has_min) {
                this.errors.giving_levels = 'Donor Level Minimum Threshold cannot be empty';
            } else if (!minGreaterThanZero) {
                this.errors.giving_levels = 'Minimum giving level must be greater than $0';
            } else if (!is_unique_num) {
                this.errors.giving_levels = 'Donor Level Minimum Threshold cannot be duplicate';
            }
            const result = has_name && is_unique_name && has_min && minGreaterThanZero && is_unique_num;
            this.if_can_save_new_level = result;
            return result;
        },
        changeDonorLevelInputs(){
            this.errors.giving_levels = '';
            this.if_can_save_new_level = true;
        },
        resetLocalLevel() {
            this.local_level = {
                name: '',
                min: '',
                report: false
            };
        },
        resetAndSortLevels() {
            const levels = this.local_client.giving_levels.levels;

            //Sort the levels based upon the min in decending order
            levels.sort((a, b) => parseFloat(b.min) - parseFloat(a.min));

            //If there are three or less, set all but the last one to have report as true
            if(levels.length <= 3)
                for(let i = 0; i < levels.length-1; i++)
                    levels[i].report = true;

            this.local_client.giving_levels.levels = levels;
        },
        cleanUpPaste(e) {
            // Prevent the default pasting event and stop bubbling
            e.preventDefault()
            e.stopPropagation()
            // Get the clipboard data
            const paste = (e.clipboardData || window.clipboardData).getData('text/plain')
            const cleanedPaste = paste.replace(/[^\d.-]/g, '');

            if (document.queryCommandSupported("insertText")) {
                document.execCommand("insertText", false, cleanedPaste);
            } else {
                document.execCommand("paste", false, cleanedPaste);
            }
        },
        save() {
            // Only save if user has edit permissions
            if (!this.canEdit('settings')) {
                return;
            }

            // If currently editing a level, commit the row edit first (validation happens in checkIfCanSaveLevel)
            if (this.edit_index !== -1) {
                if (!this.checkIfCanSaveLevel(this.edit_index)) {
                    return;
                }
                this.saveLevel(this.edit_index);
            }

            // Don't save if form is already busy
            if (this.form.busy) {
                return;
            }

            this.form.busy = true;

            window.axios.put('/api/client/' + this.local_client.url, this.local_client)
                .then(response => {
                    this.form.busy = false;
                    this.form.successful = true;
                    setTimeout(() => { this.form.successful = false }, 5000);
                    this.showNotification({
                        text: 'Your organizational settings were successfully saved!'
                    });
                }).catch(error => {
                    this.form.busy = false;
                    this.form.error = true;
                    setTimeout(() => { this.form.error = false }, 10000);
                    this.showNotification({
                        text: 'Failed to save your settings. Please try again.',
                        type: 'error'
                    });
                });
        },
        hasValidationErrors() {
            // Check if currently editing
            if (this.edit_index !== -1) {
                return !this.checkIfCanSaveLevel(this.edit_index);
            }
            return false;
        }
    }
});
</script>

